import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  useCreateProgramTemplateMutation,
  useGetCompatibleTemplateList,
  useGetListOfActivitiesById,
  useGetListOfDeliverables,
  useSearchProgramTemplateMutation,
  useUpdateProgramTemplateMutation,
} from "../../api/services/programTemplate/programTemplateApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiGrid,
  MuiModal,
  MuiTypography,
} from "../../components";
import {
  ConfirmMessageWrapper,
  CreateProgramTemplateWrapper,
} from "./CreateProgramTemplate.style";
import { CreateProgramTemplateProfile } from "./components/createProfileTemplateProfile/CreateProgramTemplateProfile";
import { CreateProgramTemplateFooter } from "./components/createProgramTemplateFooter/CreateProgramTemplateFooter";
import { CreateProgramTemplateHeader } from "./components/createProgramTemplateHeader/CreateProgramTemplateHeader";
import {
  AccordionStatusProgramTemplate,
  CompatibleResult,
  LocalizedProgramTemplateNames,
  ProgramTemplateActivitiesRowValue,
  ProgramTemplateDeliverablesRowValue,
  ProgramTemplateFormKey,
  ProgramTemplateProfileFormProps,
} from "./modelsAndSchemas/Models";
import { CreateProgramTemplateSchema } from "./modelsAndSchemas/ValidationSchema";

import { useGetCountryListForDropdowns } from "../../api/services/client/clientApis";
import { SearchProgramTemplateParams } from "../../api/services/programTemplate/models";
import PermissionWrapper from "../../components/permissionWrapper/PermissionWrapper";
import {
  BE_ERROR_CODES,
  EQUAL_TO_FILTER_SITE_CORE_KEY,
  LANGUAGE_CODE,
  MODULE_PERMISSIONS,
  PAGE_IDS,
  PAGE_URLS,
  TIMEOUT_200,
} from "../../helpers/constants";
import {
  commonSiteCoreKeys,
  localeFields,
} from "../../helpers/constants/siteCoreKeys";
import {
  getParsedErrorCode,
  isNotEmpty,
  replaceHashWithValue,
  setApiDataToDropDownOption,
  trimValue,
} from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { userPreferredLanguageSelector } from "../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { KeyValueMap } from "../../redux/slices/appState/models";
import { CompatibleTemplate } from "./components/compatibleTemplate/CompatibleTemplate";
import { TemplateCountrySelection } from "./components/compatibleTemplate/components/templateCountrySelection/TemplateCountrySelection";
import { CreateActivity } from "./components/createActivity/CreateActivity";
import { CreateDeliverable } from "./components/createDeliverable/CreateDeliverable";
import { CreateProgramTemplateActivitiesAndDeliverables } from "./components/createProgramTemplateActivitiesAndDeliverables/CreateProgramTemplateActivitiesAndDeliverables";
import { DurationWarningModal } from "./components/durationWarningModal/DurationWarningModal";
import { ManageLocale } from "./components/manageLocale/ManageLocale";
import { ManageLocaleWarning } from "./components/manageLocaleWarning/ManageLocaleWarning";
import { createProgramSiteCoreKeys } from "./siteCore/SiteCoreKeys";

interface CreateProgramTemplateProps {
  isEditTemplate?: boolean;
  pageId?: string;
  templateId?: string;
  editProgramTemplateData?: ProgramTemplateProfileFormProps;
}
const CreateProgramTemplate: FC<CreateProgramTemplateProps> = ({
  pageId = PAGE_IDS.CREATE_PROGRAM_TEMPLATE,
  isEditTemplate = false,
  templateId = "",
  editProgramTemplateData,
}) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    commonLabels,
    fetchComponentTitle,
    fetchLabel,
    fetchComponentValueFromKey,
    fetchCommonLabel,
    pageTitle,
    commonValidationMessages,
  } = useSiteCore();
  const userSelectedLanguage = useSelector(userPreferredLanguageSelector);
  const isUserLanguage = userSelectedLanguage === LANGUAGE_CODE.EN_GB;

  const [accordionStatus, setAccordionStatus] =
    useState<AccordionStatusProgramTemplate>({
      profile: true,
      activity: false,
      compatibleTemplate: false,
      all: true,
    });

  const countryRef = useRef<{ resetValues: (values?: string[]) => void }>(null);
  const [showModal, setShowModal] = useState(false);
  const [showLocaleModal, setShowLocaleModal] = useState(false);

  const [manageLocalsData, setManageLocalsData] = useState<KeyValueMap>({});
  const [isManageLocalChange, setIsManageLocalChange] = useState(false);
  const [showLocaleWarning, setShowLocaleWarning] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [isOpenActivity, setIsOpenActivity] = useState(false);
  const [isOpenDeliverable, setIsOpenDeliverable] = useState(false);
  const [showActivitiesSection, setShowActivitiesSection] = useState(false);
  const [currentTemplateId, setCurrentTemplateId] = useState<
    string | undefined
  >(templateId);
  const [showDurationWarningModal, setShowDurationWarningModal] =
    useState(false);

  const [
    localLocalizedProgramTemplateNames,
    setLocalLocalizedProgramTemplateNames,
  ] = useState<LocalizedProgramTemplateNames[]>([]);

  const {
    mutateAsync: saveProgramTemplateApi,
    isPending: isSavePending,
    isError: isSaveError,
    isSuccess: isSaveSuccess,
    error: saveError,
  } = useCreateProgramTemplateMutation();

  const {
    mutateAsync: getProgramTemplateList,
    isPending: isSearchPending,
    data: searchResult, // search result will be pass to result component
    isError: isProgramTemplateError,
    isSuccess: isProgramTemplateSearchSuccess,
  } = useSearchProgramTemplateMutation();

  const {
    mutateAsync: updateProgramTemplateApi,
    isPending: isUpdatePending,
    isError: isUpdateError,
    isSuccess: isUpdateSuccess,
    error: updateError,
  } = useUpdateProgramTemplateMutation();

  const {
    data: listOfActivitiesData,
    isFetching: isSearchActivityPending,
    isError: isSearchActivityError,
    isSuccess: isSearchActivitySuccess,
    refetchWithParams: getListOfActivitiesApi,
    clearListOfActivitiesData,
  } = useGetListOfActivitiesById();

  const {
    data: listOfDeliverablesData,
    isFetching: isSearchDeliverablePending,
    isError: isSearchDeliverableError,
    refetchWithParams: getListOfDeliverablesApi,
    clearListOfDeliverablesData,
  } = useGetListOfDeliverables();

  const { data: countryListData } = useGetCountryListForDropdowns();

  const {
    refetchWithParams: getCompatibleTemplateTableList,
    data: compatibleTemplateTableList,
    isFetching: isTemplateLoading,
  } = useGetCompatibleTemplateList();

  const mandatoryNameByLanguage = useMemo(() => {
    const fields = localeFields(commonLabels);
    return fields.find(
      (placeholder) =>
        placeholder.key === userSelectedLanguage &&
        placeholder.key !== LANGUAGE_CODE.EN_US &&
        placeholder.key !== LANGUAGE_CODE.EN_GB,
    );
  }, [
    localeFields,
    userSelectedLanguage,
    commonLabels,
    editProgramTemplateData,
  ]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isSavePending || isUpdatePending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isSavePending, isUpdatePending]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isSearchActivityPending || isTemplateLoading || isSearchPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isSearchActivityPending, isTemplateLoading, isSearchPending]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isSearchDeliverablePending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isSearchDeliverablePending]);

  useEffect(() => {
    if (isSearchActivitySuccess) {
      setAccordionStatus((prevStatus) => ({
        ...prevStatus,
        activity: true,
      }));
    }
    if (isSearchActivityError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isSearchActivityError, isSearchActivitySuccess]);

  useEffect(() => {
    if (isSearchDeliverableError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isSearchDeliverableError]);

  const handleNavigateBack = () => {
    location.state?.from === PAGE_URLS.PROGRAM_TEMPLATE ||
    location.pathname.includes(PAGE_URLS.EDIT_PROGRAM_TEMPLATE)
      ? navigate(-1)
      : navigate(PAGE_URLS.DASHBOARD);
  };

  useEffect(() => {
    if (isSaveSuccess) {
      displayToast(
        fetchLabel(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.programTemplateCreatedMessage,
        ),
      );
      setAccordionStatus((prevStatus) => ({
        ...prevStatus,
        compatibleTemplate: true,
      }));
    }
    if (isUpdateSuccess) {
      const toastSuccessMessage = isEditTemplate
        ? fetchLabel(
            createProgramSiteCoreKeys.profileSection,
            createProgramSiteCoreKeys.programTemplateUpdatedMessage,
          )
        : fetchLabel(
            createProgramSiteCoreKeys.profileSection,
            createProgramSiteCoreKeys.programTemplateSaveMessage,
          );
      displayToast(toastSuccessMessage);
      handleNavigateBack();
    }

    if (isSaveError || isUpdateError || isProgramTemplateError) {
      const errorObj = isSaveError ? saveError : updateError;
      const errorCode = getParsedErrorCode(errorObj);
      if (errorCode === BE_ERROR_CODES.PROGRAM_NAME_UNIQUE) {
        setError(ProgramTemplateFormKey.programName, {
          type: "custom",
          message: fetchLabel(
            createProgramSiteCoreKeys.profileSection,
            createProgramSiteCoreKeys.programTemplateDuplicateCheckMessage,
          ),
        });
      } else if (
        errorCode === BE_ERROR_CODES.PROGRAM_TEMPLATE_NEXT_VIEW_NOT_EDITABLE
      ) {
        const nextViewErrorMessage = fetchLabel(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.nextViewNotEditableErrorMessage,
        );
        displayToast(nextViewErrorMessage, "error");
      } else {
        displayToast(
          commonLabels.someThingWentWrongPleaseTryAgainLater,
          "error",
        );
        dispatch(toggleLoader({ isVisible: false }));
      }
    }
  }, [
    isSaveSuccess,
    isSaveError,
    isUpdateError,
    isUpdateSuccess,
    isProgramTemplateError,
  ]);

  useEffect(() => {
    if (currentTemplateId && isSaveSuccess) {
      getListOfActivitiesApi(currentTemplateId, getValues().programFrameWork);
      getListOfDeliverables(currentTemplateId, getValues().programFrameWork);
      setShowActivitiesSection(true);
    }
  }, [isSaveSuccess, currentTemplateId]);

  useEffect(() => {
    if (isProgramTemplateSearchSuccess) {
      const isDuplicate = searchResult?.data.length !== 0;
      lastCheckWasDuplicateRef.current = isDuplicate;
      if (isDuplicate) {
        setError(ProgramTemplateFormKey.programName, {
          type: "custom",
          message: fetchLabel(
            createProgramSiteCoreKeys.profileSection,
            createProgramSiteCoreKeys.programTemplateDuplicateCheckMessage,
          ),
        });
      }
    }
  }, [isProgramTemplateSearchSuccess]);

  const method = useForm<ProgramTemplateProfileFormProps>({
    mode: "all",
    defaultValues: {
      [ProgramTemplateFormKey.programName]: "",
      [ProgramTemplateFormKey.lineOfBusiness]: "",
      [ProgramTemplateFormKey.programFrameWork]: "",
      [ProgramTemplateFormKey.solutionCategory]: "",
      [ProgramTemplateFormKey.solutionCategoryLob]: "",
      [ProgramTemplateFormKey.solutionOffering]: "",
      [ProgramTemplateFormKey.programClass]: "",
      [ProgramTemplateFormKey.duration]: undefined,
      [ProgramTemplateFormKey.status]: "",
      [ProgramTemplateFormKey.country]: [] as string[],
      [ProgramTemplateFormKey.programNameByLanguage]: "",
      [ProgramTemplateFormKey.closingReasonType]: "",
      [ProgramTemplateFormKey.nextAccess]: "",
      [ProgramTemplateFormKey.selectRegionType]: "",
    },
    resolver: yupResolver(
      CreateProgramTemplateSchema(
        commonValidationMessages,
        mandatoryNameByLanguage?.key as string,
        showActivitiesSection,
      ),
    ),
  });

  const {
    formState: { isDirty, errors },
    control,
    setValue,
    clearErrors,
    handleSubmit,
    getValues,
    reset,
    watch,
    setError,
    trigger,
  } = method;

  const englishNameWatch = useWatch({
    control,
    name: ProgramTemplateFormKey.programName,
  });

  useEffect(() => {
    const targetLocale = isUserLanguage
      ? LANGUAGE_CODE.EN_GB
      : LANGUAGE_CODE.EN_US;

    // Only update if the key already exists in manageLocalsData
    if (manageLocalsData.hasOwnProperty(targetLocale)) {
      setManageLocalsData((prev) => ({
        ...prev,
        [targetLocale]: englishNameWatch,
      }));
    }
  }, [englishNameWatch]);

  useEffect(() => {
    if (editProgramTemplateData && isEditTemplate) {
      reset(editProgramTemplateData); // Dynamically update form values
      getListOfActivitiesApi(
        templateId,
        editProgramTemplateData.programFrameWork,
      ); // to get the activity list

      getListOfDeliverables(
        templateId,
        editProgramTemplateData.programFrameWork,
      );
      setLocalLocalizedProgramTemplateNames(
        editProgramTemplateData.localizedProgramTemplateNames ?? [],
      );

      setManageLocalsData(
        convertArrayToLocaleObject(
          editProgramTemplateData.localizedProgramTemplateNames ?? [],
        ),
      );

      setTimeout(() => {
        countryRef.current?.resetValues(editProgramTemplateData?.country ?? []);
      }, TIMEOUT_200); // to ensure that countryRef is available

      // if locale data available in edit data then pre populate the textbox value
      if (mandatoryNameByLanguage?.key) {
        const keyValue =
          editProgramTemplateData.localizedProgramTemplateNames?.find(
            ({ localekey: localeKey }) =>
              localeKey === mandatoryNameByLanguage.key,
          );
        setValue(
          ProgramTemplateFormKey.programNameByLanguage,
          keyValue?.name ?? "",
        );
        setNameValue(keyValue?.name ?? editProgramTemplateData.programName);
      }

      populateLanguageSpecificName(editProgramTemplateData);

      setAccordionStatus((prevStatus) => ({
        ...prevStatus,
        activity: true,
        compatibleTemplate: true,
      }));
      setShowActivitiesSection(true);
    }
  }, [editProgramTemplateData]);

  const populateLanguageSpecificName = (
    data: ProgramTemplateProfileFormProps,
  ) => {
    const nameFromLocale = (localeUkOrUs: string) =>
      data.localizedProgramTemplateNames?.find(
        ({ localekey }) => localekey === localeUkOrUs,
      )?.name;

    if (userSelectedLanguage === LANGUAGE_CODE.EN_GB) {
      setValue(
        ProgramTemplateFormKey.programName,
        nameFromLocale(LANGUAGE_CODE.EN_GB) ?? data.programName,
      );
    }

    if (userSelectedLanguage === LANGUAGE_CODE.EN_US) {
      setValue(
        ProgramTemplateFormKey.programName,
        nameFromLocale(LANGUAGE_CODE.EN_US) ?? data.programName,
      );
    }
  };

  const handleExpandCollapseAllChange = () => {
    const newStatus = !accordionStatus.all;
    setAccordionStatus({
      profile: newStatus,
      activity: newStatus,
      compatibleTemplate: newStatus,
      all: newStatus,
    });
  };

  const handleIndividualAccordionChange = (accordionName: string) => {
    setAccordionStatus((prevStatus) => ({
      ...prevStatus,
      [accordionName]: !prevStatus[accordionName],
    }));
  };

  const convertManageLocalToArray = (data: KeyValueMap, excludeKey: string) => {
    return Object.entries(data)
      .filter(
        ([key, value]) => key !== excludeKey && value && value.trim() !== "",
      ) // Exclude empty values and specified key
      .map(([localeKey, name]) => ({
        Name: name, // Use the value from the input object for "Name"
        Localekey: localeKey,
      }));
  };

  const convertArrayToLocaleObject = (
    data: LocalizedProgramTemplateNames[],
  ) => {
    return data.reduce(
      (acc, item) => {
        if (item.localekey) {
          acc[item.localekey] = item.name || "";
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  };

  const getDataForApiParam = () => {
    const {
      programName,
      lineOfBusiness,
      programFrameWork,
      solutionCategory,
      solutionCategoryLob,
      solutionOffering,
      programClass,
      duration,
      status,
      country,
      programNameByLanguage,
      closingReasonType,
      nextAccess,
    } = getValues();

    let localizedProgramTemplateNames = convertManageLocalToArray(
      manageLocalsData,
      isUserLanguage ? "" : LANGUAGE_CODE.EN_US,
    );

    let languageData = {};
    if (localizedProgramTemplateNames.length) {
      if (userSelectedLanguage === LANGUAGE_CODE.EN_US || isUserLanguage) {
        localizedProgramTemplateNames = [
          {
            Name: programName,
            Localekey: LANGUAGE_CODE.EN_US,
          },
          {
            Name: programName,
            Localekey: LANGUAGE_CODE.EN_GB,
          },
          ...localizedProgramTemplateNames,
        ];
      }
      const uniqueByLocaleKey = Array.from(
        localizedProgramTemplateNames
          .reduce((map, item) => map.set(item.Localekey, item), new Map())
          .values(),
      );

      const hasEnUS = uniqueByLocaleKey.some(
        (item) => item.Localekey === LANGUAGE_CODE.EN_US,
      );

      if (!hasEnUS) {
        uniqueByLocaleKey.push({
          Name: programName,
          Localekey: LANGUAGE_CODE.EN_US,
        });
      }

      languageData = {
        localizedProgramTemplateNames: uniqueByLocaleKey,
        entityType: 0,
      };
    } else if (isNotEmpty(mandatoryNameByLanguage)) {
      languageData = {
        localizedProgramTemplateNames: [
          {
            Name: programNameByLanguage,
            Localekey: userSelectedLanguage,
          },
        ],
        entityType: 0,
      };
    } else if (isUserLanguage) {
      languageData = {
        localizedProgramTemplateNames: [
          {
            Name: programName,
            Localekey: userSelectedLanguage,
          },
          {
            Name: programName,
            Localekey: LANGUAGE_CODE.EN_US,
          },
        ],
        entityType: 0,
      };
    } else if (!isUserLanguage) {
      languageData = {
        localizedProgramTemplateNames: [
          {
            Name: programName,
            Localekey: LANGUAGE_CODE.EN_US,
          },
          {
            Name: programName,
            Localekey: LANGUAGE_CODE.EN_GB,
          },
        ],
        entityType: 0,
      };
    }

    //take activity data from activity list
    const activitiesData = listOfActivitiesData?.map(
      (item: ProgramTemplateActivitiesRowValue) => item.id,
    );

    //take deliverable data from deliverables list
    const deliverablesData =
      listOfDeliverablesData?.deliverables?.map(
        (item: ProgramTemplateDeliverablesRowValue) => item.id,
      ) ?? [];

    return {
      name: trimValue(programName),
      lineOfBusinessId: lineOfBusiness,
      programFrameWorkId: programFrameWork,
      solutionCategoryId: solutionCategory,
      solutionCategoryLOBId: solutionCategoryLob,
      solutionOfferingId: solutionOffering,
      programClassId: programClass,
      durationInMoths: duration,
      status,
      country,
      ...languageData,
      activityIds: activitiesData,
      deliverableIds: deliverablesData,
      closingReason: closingReasonType,
      nextAccess:
        nextAccess === createProgramSiteCoreKeys.nextAccessYes ? true : false,
    };
  };

  const handleEditTemplate = () => {
    checkCompatibleTemplateForDurationUpdate();
  };

  const handleCreateTemplate = () => {
    if (!isSaveSuccess) {
      setShowModal(true);
    } else {
      // is saved then update
      checkCompatibleTemplateForDurationUpdate();
    }
  };

  const onConfirmEditClick = () => {
    handleEditTemplate();
  };

  const onConfirmCreateClick = () => {
    handleCreateTemplate();
  };

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const handleProgramFrameworkChange = async (programFrameworkId: string) => {
    if (currentTemplateId) {
      setShowModal(true);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handlePrimaryButtonClick = async () => {
    setShowModal(false);

    if (currentTemplateId) {
      clearListOfActivitiesData();
      setTimeout(() => {
        getListOfActivitiesApi(currentTemplateId, getValues().programFrameWork);
      }, 0);
      getListOfDeliverables(currentTemplateId, getValues().programFrameWork);
    } else if (!isEditTemplate && !isSaveSuccess) {
      const response = await saveProgramTemplateApi(getDataForApiParam());
      if (response?.data?.id) {
        setCurrentTemplateId(response.data.id); // Store the template ID from the API response
      }
    }
  };

  const getListOfDeliverables = (
    templateId: string,
    programFrameWork: string,
  ) => {
    clearListOfDeliverablesData();
    setTimeout(() => {
      getListOfDeliverablesApi(templateId, programFrameWork);
    }, 0);
  };

  const handlePrimaryButtonClickDeliverables = async () => {
    setShowModal(false);

    if (currentTemplateId) {
      getListOfDeliverables(currentTemplateId, getValues().programFrameWork);
    } else if (!isEditTemplate && !isSaveSuccess) {
      const response = await saveProgramTemplateApi(getDataForApiParam());
      if (response?.data?.id) {
        setCurrentTemplateId(response.data.id); // Store the template ID from the API response
      }
    }
  };

  const renderActivityHeader = () => {
    return (
      <MuiGrid container className="form-header">
        <MuiGrid size={{ xs: 6 }}>
          <MuiTypography
            variant="h2SemiBold"
            className="form-header-left"
            component="h2"
          >
            {fetchComponentTitle(createProgramSiteCoreKeys.activitiesList)}
          </MuiTypography>
        </MuiGrid>
      </MuiGrid>
    );
  };

  // get manage local data from modal popup
  const getManageLocalsData = (manageLocals: KeyValueMap) => {
    setManageLocalsData(manageLocals);
    setLocalLocalizedProgramTemplateNames((prev) =>
      prev.map((item) => ({
        ...item,
        name: manageLocals[item.localekey] ?? item.name,
      })),
    );
    const key = Object.keys(manageLocals).find(
      (key) => key === mandatoryNameByLanguage?.key,
    );

    const value = key ? manageLocals[key] : undefined;

    if (value) {
      setValue(ProgramTemplateFormKey.programNameByLanguage, value);
      clearErrors(ProgramTemplateFormKey.programNameByLanguage);
      setNameValue(value);
    } else {
      setNameValue(
        manageLocals[
          isUserLanguage ? LANGUAGE_CODE.EN_GB : LANGUAGE_CODE.EN_US
        ],
      );
    }
    setValue(
      ProgramTemplateFormKey.programName,
      manageLocals[isUserLanguage ? LANGUAGE_CODE.EN_GB : LANGUAGE_CODE.EN_US],
    );
    clearErrors(ProgramTemplateFormKey.programName);
  };

  const getActivitiesData = () => {
    return (listOfActivitiesData as ProgramTemplateActivitiesRowValue[]) || [];
  };

  const getDeliverablesData = () => {
    if (listOfDeliverablesData?.deliverables) {
      return (
        (listOfDeliverablesData.deliverables as ProgramTemplateDeliverablesRowValue[]) ||
        []
      );
    } else return [];
  };

  const handleConfirmLocaleWarning = () => {
    setShowLocaleWarning(false);
    setShowLocaleModal(true);
  };

  const lastCheckedNameRef = useRef<string>("");
  const lastCheckWasDuplicateRef = useRef<boolean | null>(null);
  const handleProgramNameOnBlur = async (value: string) => {
    const trimmedValue = trimValue(value ?? "");

    if (isEditTemplate && editProgramTemplateData?.programName === value) {
      return;
    }
    if (lastCheckedNameRef.current === trimmedValue) {
      if (lastCheckWasDuplicateRef.current) {
        clearErrors(ProgramTemplateFormKey.programName); //  force clear

        setTimeout(() => {
          setError(ProgramTemplateFormKey.programName, {
            type: "custom",
            message: fetchLabel(
              createProgramSiteCoreKeys.profileSection,
              createProgramSiteCoreKeys.programTemplateDuplicateCheckMessage,
            ),
          });
        }, 0);
      } else {
        clearErrors(ProgramTemplateFormKey.programName);
      }
      return;
    }

    const isValid = await trigger(ProgramTemplateFormKey.programName);
    if (!isValid) return;
    lastCheckedNameRef.current = trimmedValue;

    const params: SearchProgramTemplateParams = {
      name: trimmedValue,
      nameFilter: EQUAL_TO_FILTER_SITE_CORE_KEY,
      lineOfBusinessId: "",
      programFrameWorkId: "",
      countryIds: [],
      status: "",
    };
    // added this to Queue when user clicks on locale link directly then this api call get's triggered
    // loader is being loaded and then locale link click was not getting triggered
    setTimeout(() => {
      getProgramTemplateList(params);
    }, TIMEOUT_200);

    if (nameValue !== value && isEditTemplate) {
      setNameValue(value);
      setShowLocaleWarning(true);
    }
  };

  const onCreateActivityClick = () => {
    setIsOpenActivity(true);
  };

  const activityListApi = () => {
    clearListOfActivitiesData();
    setTimeout(() => {
      getListOfActivitiesApi(
        currentTemplateId ?? "",
        getValues().programFrameWork,
      );
    }, 0);
  };

  const getActivityAndDeliverableApi = () => {
    activityListApi();
    getListOfDeliverablesApiFunc();
  };
  const onCreateDeliverableClick = () => {
    setIsOpenDeliverable(true);
  };

  const getListOfDeliverablesApiFunc = () => {
    if (currentTemplateId) {
      getListOfDeliverables(currentTemplateId, getValues().programFrameWork);
    }
  };

  //below functions are for compatible template
  const skippedCountriesListForCompatible = useMemo(() => {
    return getValues().country;
  }, [watch(ProgramTemplateFormKey.country)]);

  const skippedCountriesListForProfile = useMemo(() => {
    if (isNotEmpty(compatibleTemplateTableList)) {
      const countryList = compatibleTemplateTableList.map(
        (item: CompatibleResult) => item.countryCode,
      );
      return countryList;
    }
    return [];
  }, [compatibleTemplateTableList]);

  const updateTemplateApiFunction = () => {
    updateProgramTemplateApi({
      ...getDataForApiParam(),
      id: currentTemplateId,
    });
  };
  const handleValidSubmit = () => {
    const hasProgramNameError = !!errors[ProgramTemplateFormKey.programName];
    if (isNotEmpty(searchResult?.data) && searchResult?.data.length !== 0) {
      setError(ProgramTemplateFormKey.programName, {
        type: "custom",
        message: fetchLabel(
          createProgramSiteCoreKeys.profileSection,
          createProgramSiteCoreKeys.programTemplateDuplicateCheckMessage,
        ),
      });
    }
    if (hasProgramNameError) {
      return;
    }
    if (isEditTemplate) {
      onConfirmEditClick();
    } else {
      onConfirmCreateClick();
    }
  };

  const checkCompatibleTemplateForDurationUpdate = () => {
    if (isNotEmpty(compatibleTemplateTableList)) {
      const duration = getValues().duration;
      const isDurationMatched = compatibleTemplateTableList.some(
        (item: CompatibleResult) => item.duration < duration,
      );
      if (isDurationMatched) {
        setShowDurationWarningModal(true);
      } else {
        updateTemplateApiFunction();
      }
    } else {
      updateTemplateApiFunction();
    }
  };

  let confirmButtonLabel;
  if (editProgramTemplateData) {
    confirmButtonLabel = commonLabels.update;
  } else if (!isSaveSuccess) {
    confirmButtonLabel = commonLabels.continue;
  } else {
    confirmButtonLabel = commonLabels.save;
  }

  const isMultiRegionSelected = useMemo(() => {
    const { selectRegionType } = getValues();
    return selectRegionType === createProgramSiteCoreKeys.multipleCountries;
  }, [watch(ProgramTemplateFormKey.selectRegionType)]);

  const handleRegionTypeChange = (value: string) => {
    if (value !== createProgramSiteCoreKeys.multipleCountries) {
      countryRef?.current?.resetValues([]);
      setValue(ProgramTemplateFormKey.country, []);
    }
  };

  return (
    <CreateProgramTemplateWrapper data-testid="program-template">
      <CreateProgramTemplateHeader
        allAccordionStatus={accordionStatus.all}
        pageId={pageId}
        showExpandAll={showActivitiesSection}
        handleExpandCollapseAllChange={handleExpandCollapseAllChange}
        pageTitle={
          isEditTemplate
            ? replaceHashWithValue(
                fetchLabel(
                  createProgramSiteCoreKeys.profileSection,
                  createProgramSiteCoreKeys.editProgramTemplate,
                ),
                editProgramTemplateData?.programName ?? "",
              )
            : pageTitle
        }
      />

      <PermissionWrapper
        permission={
          isEditTemplate
            ? MODULE_PERMISSIONS.EDIT_PROFILE
            : MODULE_PERMISSIONS.ADD_PROFILE
        }
      >
        <AccordionHeader
          title={renderHeader(
            fetchComponentTitle(createProgramSiteCoreKeys.profileSection),
          )}
          onChange={() => handleIndividualAccordionChange("profile")}
          isExpanded={accordionStatus.profile}
        >
          <CreateProgramTemplateProfile
            control={control}
            pageId={pageId}
            setValue={setValue}
            handleProgramFrameworkChange={handleProgramFrameworkChange}
            templateData={editProgramTemplateData}
            setShowLocaleModal={() => setShowLocaleModal(true)}
            nameMandatoryByLanguage={mandatoryNameByLanguage}
            handleProgramNameOnBlur={handleProgramNameOnBlur}
            defaultCountryList={setApiDataToDropDownOption(
              countryListData,
              true,
            )}
            skippedCountriesList={skippedCountriesListForProfile} // need to update
          />
        </AccordionHeader>
      </PermissionWrapper>

      <MuiBox className="separator-space" />
      {showActivitiesSection && (
        <>
          <PermissionWrapper
            permission={
              isEditTemplate
                ? MODULE_PERMISSIONS.EDIT_COMPATIBLE_TEMPLATES
                : MODULE_PERMISSIONS.ADD_COMPATIBLE_TEMPLATES
            }
          >
            <AccordionHeader
              title={renderHeader(
                fetchComponentTitle(
                  createProgramSiteCoreKeys.compatibleTemplateKey,
                ),
              )}
              onChange={() =>
                handleIndividualAccordionChange("compatibleTemplate")
              }
              isExpanded={accordionStatus.compatibleTemplate}
            >
              <FormProvider {...method}>
                <TemplateCountrySelection
                  pageId={pageId}
                  countryRef={countryRef}
                  defaultCountryList={setApiDataToDropDownOption(
                    countryListData,
                    true,
                  )}
                  skippedCountriesList={skippedCountriesListForProfile}
                  isMultiRegionSelected={isMultiRegionSelected}
                  handleRegionTypeChange={handleRegionTypeChange}
                />
              </FormProvider>

              {isMultiRegionSelected && (
                <CompatibleTemplate
                  pageId={pageId}
                  defaultCountryList={setApiDataToDropDownOption(
                    countryListData,
                    true,
                  )}
                  skippedCountriesList={[...skippedCountriesListForCompatible]}
                  templateId={currentTemplateId ?? ""}
                  isEditTemplate={isEditTemplate}
                  getCompatibleTemplateList={() =>
                    getCompatibleTemplateTableList({
                      templateId: currentTemplateId ?? "",
                    })
                  }
                  compatibleTemplateList={compatibleTemplateTableList ?? []} //for table result
                />
              )}
            </AccordionHeader>
          </PermissionWrapper>

          <MuiBox className="separator-space" />

          <PermissionWrapper
            permission={
              isEditTemplate
                ? MODULE_PERMISSIONS.EDIT_ACTIVITIES_AND_DELIVERABLES
                : MODULE_PERMISSIONS.ADD_ACTIVITIES_AND_DELIVERABLES
            }
          >
            <AccordionHeader
              title={renderActivityHeader()}
              onChange={() => handleIndividualAccordionChange("activity")}
              isExpanded={accordionStatus.activity}
            >
              <CreateProgramTemplateActivitiesAndDeliverables
                rows={getActivitiesData()}
                deliverableRows={getDeliverablesData()}
                onCreateActivityClick={onCreateActivityClick}
                successHandler={handlePrimaryButtonClick}
                successHandlerDeliverables={
                  handlePrimaryButtonClickDeliverables
                }
                programTemplateId={currentTemplateId}
                onCreateDeliverableClick={onCreateDeliverableClick}
                frameWorkId={getValues().programFrameWork}
              />
            </AccordionHeader>
          </PermissionWrapper>
        </>
      )}

      <CreateProgramTemplateFooter
        pageId={pageId}
        onConfirmClick={() => handleSubmit(handleValidSubmit)()}
        isFiledUpdated={isDirty || isManageLocalChange}
        handleNavigateBack={handleNavigateBack}
        confirmButtonLabel={confirmButtonLabel}
        cancelButtonLabel={
          currentTemplateId ? commonLabels.close : commonLabels.cancel
        }
      />

      {showModal && (
        <MuiModal
          open={showModal}
          onClose={handleModalClose}
          title={fetchComponentTitle(
            createProgramSiteCoreKeys.informationPopup,
          )}
          primaryButtonTitle={fetchCommonLabel(commonSiteCoreKeys.ok)}
          secondaryButtonTitle=""
          onPrimaryButtonClick={handlePrimaryButtonClick}
        >
          <ConfirmMessageWrapper>
            <MuiTypography
              id="modal-description"
              variant="h3"
              component={"h3"}
              className="confirm-message"
            >
              {fetchComponentValueFromKey(
                createProgramSiteCoreKeys.informationPopup,
                createProgramSiteCoreKeys.description,
              )}
            </MuiTypography>
          </ConfirmMessageWrapper>
        </MuiModal>
      )}

      {showLocaleModal && (
        <ManageLocale
          pageId={pageId}
          getManageLocalsData={getManageLocalsData}
          show={showLocaleModal}
          handleCloseModal={() => setShowLocaleModal(false)}
          nameMandatoryByLanguage={mandatoryNameByLanguage}
          englishName={getValues().programName}
          programNameByLanguage={getValues().programNameByLanguage}
          localizedProgramTemplateNames={
            localLocalizedProgramTemplateNames ?? []
          } // for default set edit program data
          defaultManageLocalData={manageLocalsData}
          isEditTemplate={isEditTemplate}
          setIsManageLocalChange={setIsManageLocalChange}
        />
      )}
      {showLocaleWarning && (
        <ManageLocaleWarning
          show={showLocaleWarning}
          handleCloseModal={() => setShowLocaleWarning(false)}
          onPrimaryButtonClick={handleConfirmLocaleWarning}
          message={fetchLabel(
            createProgramSiteCoreKeys.createActivityKey,
            createProgramSiteCoreKeys.updateLocaleActionMessage,
          )}
        />
      )}
      {isOpenActivity && (
        <CreateActivity
          show={isOpenActivity}
          handleCloseModal={() => setIsOpenActivity(false)}
          programTemplateId={currentTemplateId ?? ""}
          pageId={pageId}
          getListOfActivitiesApi={getActivityAndDeliverableApi}
          frameWorkId={getValues().programFrameWork}
        />
      )}

      {isOpenDeliverable && (
        <CreateDeliverable
          show={isOpenDeliverable}
          handleCloseModal={() => setIsOpenDeliverable(false)}
          programTemplateId={currentTemplateId ?? ""}
          pageId={pageId}
          getListOfDeliverablesApi={getListOfDeliverablesApiFunc}
        />
      )}

      {showDurationWarningModal && (
        <DurationWarningModal
          handleModalClose={() => setShowDurationWarningModal(false)}
          showModal={showDurationWarningModal}
        />
      )}
    </CreateProgramTemplateWrapper>
  );
};

export default CreateProgramTemplate;
